import React, { useEffect, useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { ChevronDown, Loader2, Play } from 'lucide-react';
import type { DemoEnvironmentResponse } from '../../services/api/types.gen';
import { DemoSettingsPanel } from './DemoSettingsPanel';
import { DemoEnvironmentRunDetails } from './DemoEnvironmentRunDetails';
import { KinnserPullLine } from './KinnserPullLine';
import { usePullKinnser } from './usePullKinnser';
import { distinctSystemName } from './demoEnvironmentStatus';

/**
 * One sandbox, beside the list rather than behind a gear on its row.
 *
 * The settings were a dialog: deciding what to build meant opening it, closing
 * it to pull, and opening it again -- while Run sat out on the row, so the two
 * things an operator does to an agency were in different places. Everything is
 * here now, in the order it is done: what the agency is, what to do to it, what
 * the last pull got, and what a build would make of it.
 *
 * The settings save themselves as they are edited, so there is nothing to
 * close and nothing to commit.
 */

interface Props {
  row: DemoEnvironmentResponse | null;
  /** Every agency in the list, for the settings that name another one. */
  agencies: readonly DemoEnvironmentResponse[];
  /** A run is starting or in progress, so the actions are held shut. */
  busy: boolean;
  onRun: (environment: DemoEnvironmentResponse) => void;
  onSaved: () => void;
  onPulled: () => void;
}

export const DemoAgencyPane: React.FC<Props> = ({
  row,
  agencies,
  busy,
  onRun,
  onSaved,
  onPulled,
}) => {
  const {
    pull,
    pulling,
    rescrapeAndPull,
    scraping,
    result: pullResult,
    error: pullError,
    reset: resetPull,
  } = usePullKinnser();
  const pullBusy = pulling || scraping;
  const [saving, setSaving] = useState(false);
  // An edit the panel has not saved yet. The settings save themselves on a
  // timer, so without this Run setup would start a build from the settings the
  // operator has just replaced.
  const [pendingEdits, setPendingEdits] = useState(false);

  // Keyed on the agency: a pull made here belongs to the agency it was made
  // for, and moving to another one must not show it theirs.
  const agencyId = row?.agency_id ?? null;
  useEffect(() => {
    resetPull();
  }, [agencyId, resetPull]);

  if (!row) {
    return (
      <p className='rounded-md border p-6 text-sm text-muted-foreground'>
        Pick an agency to see its sandbox.
      </p>
    );
  }

  // A pull just made here, else the one saved on the config: the same pull
  // either way, so the line reads the same after a build and a reload.
  const latestPull = pullResult ?? row.kinnser_pull ?? null;
  const name = row.agency_written_name || row.agency_system_name;
  const systemName = distinctSystemName(
    row.agency_written_name,
    row.agency_system_name
  );

  return (
    <div className='space-y-3'>
      <div className='space-y-2 rounded-md border bg-muted/30 px-3 py-2'>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
          <div className='min-w-0'>
            <div className='flex flex-wrap items-baseline gap-2'>
              <h2 className='truncate text-sm font-semibold' title={name}>
                {name}
              </h2>
              {systemName && (
                <span className='font-mono text-xs text-muted-foreground'>
                  {systemName}
                </span>
              )}
            </div>
            <KinnserPullLine
              pull={latestPull}
              setupAt={row.setup_at}
              error={pullError}
            />
          </div>

          {/* The two things an operator comes here to do, together, at the top
              -- rather than Run on the row and Pull halfway down a dialog. */}
          <div className='flex shrink-0 flex-wrap items-center gap-2'>
            <div className='flex items-center'>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='rounded-r-none'
                disabled={pullBusy || saving}
                onClick={async () => {
                  if (await pull(row.agency_id)) onPulled();
                }}
              >
                {pullBusy && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                {scraping ? 'Scraping Kinnser…' : 'Pull from Kinnser'}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    className='-ml-px rounded-l-none px-2'
                    disabled={pullBusy || saving}
                    aria-label='More pull options'
                  >
                    <ChevronDown className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onSelect={async () => {
                      if (await rescrapeAndPull(row.agency_id)) onPulled();
                    }}
                  >
                    Re-run scrape and pull
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button
              type='button'
              size='sm'
              disabled={busy || pullBusy || pendingEdits}
              title={pendingEdits ? 'Saving your last change…' : undefined}
              onClick={() => onRun(row)}
            >
              <Play className='mr-2 h-4 w-4' />
              Run setup
            </Button>
          </div>
        </div>
      </div>

      {/* What the last run did, where the agency it ran on already is. */}
      <DemoEnvironmentRunDetails environment={row} />

      <DemoSettingsPanel
        // Keyed on the agency so moving to another one mounts a fresh form
        // rather than showing the last one's values.
        key={row.agency_id}
        environment={row}
        agencies={agencies}
        pull={latestPull}
        pulling={pullBusy}
        onSaved={onSaved}
        onSavingChange={setSaving}
        onPendingChange={setPendingEdits}
      />
    </div>
  );
};

export default DemoAgencyPane;
